import { motion } from 'framer-motion';
import React, { useState } from 'react';

import avatar from '../../assets/images/Muser.jpg';
import alternativeIndustry1 from '../../assets/images/roadmap/image1.png';
import alternativeIndustry2 from '../../assets/images/roadmap/image2.png';
import alternativeIndustry3 from '../../assets/images/roadmap/image3.png';
import alternativeIndustry4 from '../../assets/images/roadmap/image4.png';
import { useUserProfileContext } from '../../hooks/user-user-profile-context.hooks';
import { currencyConvertor } from '../../redux/slice/roadmap-ai/roadmap-ai.operations';
import { AlternateCareer } from '../../redux/slice/roadmap-ai/roadmap-ai.type';
import { GenericPopup } from '../Popup/GenericPopup';

type ProfilePopupProps = {
  name: string;
  email: string;
  alternateData?: AlternateCareer[];
};

const ProfilePopup: React.FC<ProfilePopupProps> = ({ name, email, alternateData }) => {
  const [subPopupOpen, setSubPopupOpen] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState<{
    career: AlternateCareer;
    index: number;
  } | null>(null);
  const userProfileContext = useUserProfileContext();

  const imageArray = [
    alternativeIndustry1,
    alternativeIndustry2,
    alternativeIndustry3,
    alternativeIndustry4,
  ];
  const careerOptions: AlternateCareer[] = alternateData ?? [];

  const handleBoxClick = (career: AlternateCareer, index: number) => {
    setSelectedCareer({ career, index });
    setSubPopupOpen(true);
  };

  return (
    <div className="flex flex-col items-center p-4 w-full max-w-md mx-auto">
      {/* Profile Image */}
      <img
        className="h-20 md:h-36 w-20 md:w-36 object-cover rounded-full mb-3"
        src={userProfileContext?.profileImagePath || avatar}
        alt="Profile"
      />

      {/* Name + Email */}
      <h2 className="mt-4 text-xl font-semibold text-gray-800">{name}</h2>
      <p className="text-gray-500 text-sm">{email}</p>

      {/* Career Option Boxes */}
      <div className="grid md:grid-cols-2 grid-cols-2 gap-10 mt-12 w-full">
        {careerOptions.map((career, idx) => (
          <motion.div
            key={idx}
            onClick={() => handleBoxClick(career, idx)}
            className="cursor-pointer p-6 bg-[#E9ECFE] flex items-center justify-center border-2 border-dashed border-[#093FB4] rounded-lg text-gray-700 font-medium hover:border-blue-500 hover:text-blue-500 transition text-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
          >
            {career.career_title}
          </motion.div>
        ))}
      </div>

      {/* Career Details Popup */}
      <GenericPopup isOpen={subPopupOpen} onClose={() => setSubPopupOpen(false)}>
        {selectedCareer && (
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {/* Header */}
            {/* Image + Title */}
            <motion.img
              src={imageArray[selectedCareer.index % imageArray.length]}
              alt={selectedCareer.career.career_title}
              className="object-cover w-1/2 m-auto "
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 120 }}
            />
            <hr className="my-6" />

            <motion.div
              className="flex flex-col md:flex-row justify-between items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h2 className="text-xl  font-bold text-gray-900">
                {selectedCareer.career.career_title}
              </h2>

              {/* Confidence Circle */}
              <motion.div
                className="flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <div className="relative w-20 h-20">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="#E5E7EB"
                      strokeWidth="8"
                      fill="transparent"
                    />
                    <motion.circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="#1D4ED8"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={2 * Math.PI * 36}
                      strokeDashoffset={
                        2 * Math.PI * 36 * (1 - (selectedCareer.career.confidence || 0) / 100)
                      }
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: (selectedCareer.career.confidence || 0) / 100 }}
                      transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.3 }}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center font-bold text-lg">
                    {selectedCareer.career.confidence}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Match</p>
              </motion.div>
            </motion.div>

            <hr className="my-6" />

            {/* Salary + Growth */}
            <motion.div
              className="flex flex-col md:flex-row justify-between items-center text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <p className="text-green-800 font-medium">
                <span className="text-gray-600">Base Salary: </span>$
                {Number(currencyConvertor(selectedCareer.career.base_salary)) / 1000}k
              </p>
              <p className="font-bold text-gray-900">
                Growth Rate: {selectedCareer.career.growth_rate}
              </p>
            </motion.div>

            <hr className="my-6" />

            {/* Best Qualities */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              <h3 className="font-semibold text-gray-900 mb-3">Your Leading Strengths</h3>
              <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                {selectedCareer.career.best_quality?.map((item, idx) => {
                  const formattedItem = item
                    .replace(/_/g, ' ') // Replace underscores with spaces
                    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word

                  return (
                    <motion.div
                      key={idx}
                      className="bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * idx, duration: 0.3 }}
                      whileHover={{ scale: 1.06 }}
                    >
                      ✔ {formattedItem}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            <hr className="my-6" />

            {/* Summary */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
            >
              <h3 className="font-semibold text-gray-900 mb-3">Why this profile fits you</h3>
              <p className="text-gray-700">{selectedCareer.career.short_summary}</p>
            </motion.div>
          </motion.div>
        )}
      </GenericPopup>
    </div>
  );
};

export default ProfilePopup;
